import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { api, euro } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShoppingBasket, Trash2, History as HistoryIcon, TrendingDown, TrendingUp, ArrowRight } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Tooltip, YAxis } from "recharts";

export default function Baskets() {
  const nav = useNavigate();
  const [baskets, setBaskets] = useState([]);
  const [history, setHistory] = useState([]);
  const [budget, setBudget] = useState({});

  const load = () => {
    api.get("/baskets").then((r) => setBaskets(r.data)).catch(() => {});
    api.get("/history").then((r) => setHistory(r.data)).catch(() => {});
    api.get("/baskets/budget-alerts").then((r) => {
      const map = {};
      (r.data || []).forEach((x) => { map[x.basket_id] = x; });
      setBudget(map);
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    await api.delete(`/baskets/${id}`);
    toast.success("Panier supprimé");
    load();
  };

  const reuse = (b) => {
    const items = b.items.map((i) => ({ ...i, name: "", unit: "" }));
    localStorage.setItem("pmc_basket", JSON.stringify(items));
    toast.success("Panier chargé");
    nav("/comparateur");
  };

  return (
    <div className="App min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <h1 className="font-display text-3xl font-800 tracking-tight text-secondary">Mes paniers</h1>
        <p className="mt-1 text-muted-foreground">Retrouvez vos listes sauvegardées et votre historique de comparaisons.</p>

        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="baskets-grid">
          {baskets.map((b, i) => (
            <motion.div key={b.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="rounded-xl border border-border bg-card p-6 transition-transform duration-300 hover:-translate-y-1 hover:shadow-md" data-testid={`saved-basket-${b.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary"><ShoppingBasket className="h-5 w-5" /></div>
                  <div>
                    <h3 className="font-display text-lg font-700 text-secondary">{b.name}</h3>
                    <p className="text-xs text-muted-foreground">{b.items.length} articles</p>
                  </div>
                </div>
                <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => del(b.id)} data-testid={`delete-basket-${b.id}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
              {budget[b.id] && (
                <div className="mt-4 rounded-lg bg-muted/50 p-3" data-testid={`budget-trend-${b.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Budget estimé (meilleur prix)</span>
                    <span className="font-display font-700 text-secondary">{euro(budget[b.id].current_total)}</span>
                  </div>
                  {budget[b.id].delta !== null && budget[b.id].delta !== 0 ? (
                    <div className="mt-2 flex items-center gap-1.5">
                      {budget[b.id].increased ? (
                        <Badge className="gap-1 bg-destructive/10 text-destructive hover:bg-destructive/10" data-testid={`budget-up-${b.id}`}>
                          <TrendingUp className="h-3 w-3" /> +{euro(budget[b.id].delta)}
                          {budget[b.id].delta_pct ? ` (+${budget[b.id].delta_pct}%)` : ""}
                        </Badge>
                      ) : (
                        <Badge className="gap-1 bg-primary/10 text-primary hover:bg-primary/10" data-testid={`budget-down-${b.id}`}>
                          <TrendingDown className="h-3 w-3" /> {euro(budget[b.id].delta)}
                          {budget[b.id].delta_pct ? ` (${budget[b.id].delta_pct}%)` : ""}
                        </Badge>
                      )}
                      <span className="text-[10px] text-muted-foreground">vs {budget[b.id].previous_date ? new Date(budget[b.id].previous_date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) : "précédent"}</span>
                    </div>
                  ) : (
                    <p className="mt-2 text-[10px] text-muted-foreground">Suivi activé — on vous alertera si le budget augmente.</p>
                  )}
                  {budget[b.id].history && budget[b.id].history.length >= 2 && (
                    <div className="mt-3 h-16" data-testid={`budget-chart-${b.id}`}>
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={budget[b.id].history.map((h) => ({ date: new Date(h.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }), total: h.total }))}>
                          <YAxis hide domain={["dataMin - 0.5", "dataMax + 0.5"]} />
                          <Tooltip formatter={(v) => euro(v)} labelStyle={{ fontSize: 11 }} contentStyle={{ fontSize: 11, borderRadius: 8 }} />
                          <Line type="monotone" dataKey="total" stroke="#10b981" strokeWidth={2} dot={{ r: 2 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>
              )}
              <Button className="mt-4 w-full rounded-full" variant="outline" onClick={() => reuse(b)} data-testid={`reuse-basket-${b.id}`}>
                Réutiliser <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </motion.div>
          ))}
          {baskets.length === 0 && (
            <div className="col-span-full rounded-xl border border-dashed border-border p-12 text-center text-muted-foreground">
              Aucun panier sauvegardé. Créez-en un depuis le comparateur.
            </div>
          )}
        </div>

        <h2 className="mt-12 flex items-center gap-2 font-display text-xl font-800 text-secondary"><HistoryIcon className="h-5 w-5 text-primary" /> Historique</h2>
        <div className="mt-4 space-y-2" data-testid="history-list">
          {history.map((h) => (
            <div key={h.id} className="flex items-center justify-between rounded-lg border border-border bg-card p-4" data-testid={`history-${h.id}`}>
              <div>
                <p className="text-sm font-semibold text-secondary">{h.best_store}</p>
                <p className="text-xs text-muted-foreground">{new Date(h.created_at).toLocaleString("fr-FR")} · {h.item_count} articles</p>
              </div>
              <div className="text-right">
                <p className="font-display text-lg font-700 text-secondary">{euro(h.best_total)}</p>
                {h.savings > 0 && <Badge variant="outline" className="border-primary/30 text-primary"><TrendingDown className="mr-1 h-3 w-3" />{euro(h.savings)}</Badge>}
              </div>
            </div>
          ))}
          {history.length === 0 && <p className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">Aucune comparaison enregistrée</p>}
        </div>
      </div>
    </div>
  );
}
